import React from 'react';
import { Link } from 'react-router-dom';
import {
    Calendar,
    CheckCircle2,
    Mail,
    MessageSquare,
    Wrench,
    AlertCircle,
    ArrowRight,
} from 'lucide-react';
import TopBar from '../components/landing/TopBar';
import Navbar from '../components/landing/Navbar';
import Footer from '../components/landing/Footer';

const integrationCards = [
    {
        title: 'Twilio',
        subtitle: 'SMS automation',
        description: 'Send and receive text messages with leads directly inside your inbox.',
        icon: MessageSquare,
        badge: 'Connected in Settings',
    },
    {
        title: 'SendGrid',
        subtitle: 'Email automation',
        description: 'Deliver emails, track replies, and keep lead conversations in one place.',
        icon: Mail,
        badge: 'Connected in Settings',
    },
    {
        title: 'Calendly',
        subtitle: 'Meeting scheduling',
        description: 'Connect your calendar flow and sync upcoming meetings to your dashboard.',
        icon: Calendar,
        badge: 'OAuth connection',
    },
];

const SectionShell = ({ id, eyebrow, title, children }) => (
    <section id={id} className="py-12 md:py-16 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-flex items-center px-3 py-1 text-xs font-semibold tracking-wide rounded-full bg-gray-100 text-gray-700">
                {eyebrow}
            </span>
            <h2 className="mt-4 text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-gray-900">
                {title}
            </h2>
            <div className="mt-6">{children}</div>
        </div>
    </section>
);

const BulletList = ({ items }) => (
    <ul className="space-y-2 text-sm sm:text-base text-gray-700">
        {items.map((item) => (
            <li key={item} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-gray-900 flex-shrink-0" />
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

function IntegrationsDocs() {
    return (
        <div className="bg-gray-50 min-h-screen">
            <TopBar />
            <Navbar />

            <main>
                <section className="py-16 md:py-24 border-b border-gray-200 bg-white">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="max-w-3xl">
                            <span className="inline-flex items-center px-3 py-1 text-xs font-semibold tracking-wide rounded-full bg-gray-100 text-gray-700">
                                Integrations Guide
                            </span>
                            <h1 className="mt-5 text-4xl sm:text-5xl md:text-6xl leading-tight tracking-[-0.02em] font-semibold text-gray-900">
                                Integrate Your Communication Stack
                            </h1>
                            <p className="mt-6 text-base sm:text-lg text-gray-600 leading-relaxed">
                                Connect Twilio, SendGrid, and Calendly to automate follow-ups,
                                keep replies organized, and stay on top of meetings without
                                switching tools.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link
                                    to="/settings?section=integrations"
                                    className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg text-sm sm:text-base font-medium hover:bg-gray-800 transition-colors"
                                >
                                    Get Started
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-2">
                            <a href="#overview" className="px-3 py-1.5 text-xs sm:text-sm rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200">Overview</a>
                            <a href="#inbound-setup" className="px-3 py-1.5 text-xs sm:text-sm rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200">Inbound & DNS</a>
                            <a href="#twilio" className="px-3 py-1.5 text-xs sm:text-sm rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200">Twilio</a>
                            <a href="#sendgrid" className="px-3 py-1.5 text-xs sm:text-sm rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200">SendGrid</a>
                            <a href="#calendly" className="px-3 py-1.5 text-xs sm:text-sm rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200">Calendly</a>
                            <a href="#troubleshooting" className="px-3 py-1.5 text-xs sm:text-sm rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200">Troubleshooting</a>
                        </div>
                    </div>
                </section>

                <SectionShell id="overview" eyebrow="Overview" title="Why integrations matter in Kaeros">
                    <p className="max-w-3xl text-sm sm:text-base text-gray-700 leading-relaxed">
                        Integrations connect your outreach channels to one workflow. Your team can
                        send texts, send emails, receive replies, and track meetings in one place,
                        so no lead gets lost.
                    </p>

                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                        {integrationCards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <article key={card.title} className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
                                    <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-gray-800" />
                                    </div>
                                    <h3 className="mt-4 text-lg font-semibold text-gray-900">{card.title}</h3>
                                    <p className="text-sm text-gray-500">{card.subtitle}</p>
                                    <p className="mt-3 text-sm text-gray-700 leading-relaxed">{card.description}</p>
                                    <div className="mt-4 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                        {card.badge}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </SectionShell>


                <SectionShell id="twilio" eyebrow="Twilio" title="Set up SMS automation">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white border border-gray-200 rounded-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">What it does</h3>
                            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                                Twilio powers your text conversations with leads. You can send
                                outbound SMS and receive inbound replies in your inbox.
                            </p>

                            <h4 className="mt-5 text-sm font-semibold text-gray-900">Prerequisites</h4>
                            <BulletList
                                items={[
                                    'A Twilio account',
                                    'An active Twilio phone number',
                                    'Your Account SID and Auth Token',
                                ]}
                            />
                        </div>

                        <div className="bg-white border border-gray-200 rounded-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">Step-by-step setup</h3>
                            <BulletList
                                items={[
                                    'Open Settings and choose Integrations.',
                                    'In Twilio / SendGrid, add your Twilio Account SID.',
                                    'Add your Twilio Auth Token and phone number.',
                                    'Click Connect or Update to save.',
                                    'Send a test message to confirm delivery.',
                                ]}
                            />

                            <h4 className="mt-5 text-sm font-semibold text-gray-900">How it works in the app</h4>
                            <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                                New incoming texts are linked to the right lead and appear in Inbox.
                                Your team can reply from the same conversation view.
                            </p>

                            <h4 className="mt-5 text-sm font-semibold text-gray-900">Common issues</h4>
                            <BulletList
                                items={[
                                    'Invalid SID or token: recheck copied values.',
                                    'No inbound texts: verify webhook URL in Twilio console.',
                                    'Messages fail: confirm your Twilio number can send to that region.',
                                ]}
                            />
                        </div>
                    </div>
                </SectionShell>

                <SectionShell id="sendgrid" eyebrow="SendGrid" title="Set up email automation">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white border border-gray-200 rounded-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">What it does</h3>
                            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                                SendGrid lets you send branded emails and receive replies inside your
                                lead conversations.
                            </p>

                            <h4 className="mt-5 text-sm font-semibold text-gray-900">Prerequisites</h4>
                            <BulletList
                                items={[
                                    'A SendGrid account',
                                    'An API key with Mail Send access',
                                    'A verified sender email address',
                                ]}
                            />
                        </div>

                        <div className="bg-white border border-gray-200 rounded-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">Step-by-step setup</h3>
                            <BulletList
                                items={[
                                    'Open Settings and choose Integrations.',
                                    'Add your SendGrid API key and From Email.',
                                    'Optional: add an inbound domain for reply handling.',
                                    'Click Connect or Update to save.',
                                    'Send a test email from Inbox.',
                                ]}
                            />

                            <h4 className="mt-5 text-sm font-semibold text-gray-900">Inbound reply handling</h4>
                            <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                                When inbound is enabled, lead replies are automatically routed back to
                                the correct conversation so your team can continue the thread.
                            </p>

                            <h4 className="mt-5 text-sm font-semibold text-gray-900">Common issues</h4>
                            <BulletList
                                items={[
                                    'API key rejected: create a new key with correct permissions.',
                                    'From Email blocked: make sure sender identity is verified.',
                                    'Replies not arriving: confirm inbound parse domain and route.',
                                ]}
                            />
                        </div>
                    </div>
                </SectionShell>

                <SectionShell id="inbound-setup" eyebrow="Inbound Setup" title="Inbound Setup & DNS Configuration">
                    <div className="space-y-6">
                        <article className="bg-white border border-gray-200 rounded-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">1. What is inbound?</h3>
                            <p className="mt-3 text-sm sm:text-base text-gray-700 leading-relaxed">
                                Inbound means receiving replies from your leads.
                                SMS replies come through Twilio.
                                Email replies come through SendGrid.
                            </p>
                        </article>

                        <article className="bg-white border border-gray-200 rounded-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">2. Twilio inbound SMS setup</h3>
                            <p className="mt-3 text-sm text-gray-700">
                                What you need: a Twilio phone number.
                            </p>
                            <ol className="mt-3 space-y-2 text-sm text-gray-700 list-decimal pl-5">
                                <li>Login to Twilio.</li>
                                <li>Go to Phone Numbers and open Active Numbers.</li>
                                <li>Select your number.</li>
                                <li>Find Messaging Configuration.</li>
                                <li>Set "A message comes in" to Webhook URL and Method to POST.</li>
                            </ol>

                            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <p className="text-xs font-medium text-gray-700">Webhook URL placeholder</p>
                                <p className="mt-1 text-xs text-gray-600">
                                    Webhook URL will be available inside your app after connecting Twilio.
                                </p>
                            </div>

                            <p className="mt-3 text-xs text-red-600">
                                Without this step, replies will not appear in your inbox.
                            </p>
                        </article>

                        <article className="bg-white border border-gray-200 rounded-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">3. SendGrid inbound + DNS setup</h3>

                            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
                                <h4 className="text-sm font-semibold text-gray-900">A) DNS (MX records)</h4>
                                <p className="mt-2 text-sm text-gray-700">
                                    Add an MX record in your domain provider (GoDaddy, Namecheap, or similar).
                                </p>
                                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
                                    <div className="bg-white border border-gray-200 rounded p-2"><span className="font-semibold">Type:</span> MX</div>
                                    <div className="bg-white border border-gray-200 rounded p-2"><span className="font-semibold">Host:</span> reply</div>
                                    <div className="bg-white border border-gray-200 rounded p-2"><span className="font-semibold">Value:</span> mx.sendgrid.net</div>
                                    <div className="bg-white border border-gray-200 rounded p-2"><span className="font-semibold">Priority:</span> 10</div>
                                </div>
                                <p className="mt-2 text-xs text-gray-600">
                                    This allows SendGrid to receive incoming emails.
                                </p>
                            </div>

                            <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
                                <h4 className="text-sm font-semibold text-gray-900">B) SendGrid Inbound Parse</h4>
                                <ol className="mt-2 space-y-2 text-sm text-gray-700 list-decimal pl-5">
                                    <li>Go to SendGrid Dashboard.</li>
                                    <li>Open Settings and choose Inbound Parse.</li>
                                    <li>Add host and destination URL.</li>
                                </ol>

                                <div className="mt-3 text-xs bg-white border border-gray-200 rounded p-3 space-y-1">
                                    <p><span className="font-semibold">Host:</span> reply.yourdomain.com</p>
                                    <p><span className="font-semibold">Destination URL:</span> Webhook URL will be available inside your app after connecting SendGrid.</p>
                                </div>
                            </div>
                        </article>

                        <article className="bg-white border border-gray-200 rounded-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">4. Common mistakes</h3>
                            <BulletList
                                items={[
                                    'Wrong webhook URL',
                                    'DNS not propagated yet',
                                    'Using GET instead of POST',
                                    'Missing domain setup',
                                ]}
                            />
                        </article>
                    </div>
                </SectionShell>
                <SectionShell id="calendly" eyebrow="Calendly" title="Set up meeting sync">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white border border-gray-200 rounded-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">What it does</h3>
                            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                                Calendly connection pulls your scheduled meetings into Kaeros
                                so you can track appointments next to lead activity.
                            </p>

                            <h4 className="mt-5 text-sm font-semibold text-gray-900">OAuth connection flow</h4>
                            <BulletList
                                items={[
                                    'Click Connect Calendly in Settings.',
                                    'Sign in to Calendly and approve access.',
                                    'Return to Kaeros and confirm Connected status.',
                                ]}
                            />
                        </div>

                        <div className="bg-white border border-gray-200 rounded-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">Sync and dashboard view</h3>
                            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                                After connection, meetings are synced to your account. Upcoming
                                meetings appear on your dashboard and can be refreshed anytime.
                            </p>

                            <h4 className="mt-5 text-sm font-semibold text-gray-900">Common issues</h4>
                            <BulletList
                                items={[
                                    'Connection denied: restart Connect and approve permissions.',
                                    'No meetings shown: use Sync Now and refresh dashboard.',
                                    'Sync error: disconnect and reconnect Calendly.',
                                ]}
                            />
                        </div>
                    </div>
                </SectionShell>

                <SectionShell id="troubleshooting" eyebrow="Support" title="Troubleshooting quick fixes">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        <article className="bg-white border border-gray-200 rounded-2xl p-5">
                            <div className="flex items-center gap-2 text-gray-900 font-semibold">
                                <Wrench className="w-4 h-4" />
                                Webhooks not working
                            </div>
                            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                                Recheck webhook URLs in provider dashboards and ensure your integration is saved in Settings.
                            </p>
                        </article>

                        <article className="bg-white border border-gray-200 rounded-2xl p-5">
                            <div className="flex items-center gap-2 text-gray-900 font-semibold">
                                <AlertCircle className="w-4 h-4" />
                                Invalid credentials
                            </div>
                            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                                Paste fresh credentials directly from Twilio or SendGrid, then click Update to save changes.
                            </p>
                        </article>

                        <article className="bg-white border border-gray-200 rounded-2xl p-5">
                            <div className="flex items-center gap-2 text-gray-900 font-semibold">
                                <MessageSquare className="w-4 h-4" />
                                No messages syncing
                            </div>
                            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                                Confirm the connected phone number and sender email match your active provider configuration.
                            </p>
                        </article>

                        <article className="bg-white border border-gray-200 rounded-2xl p-5">
                            <div className="flex items-center gap-2 text-gray-900 font-semibold">
                                <Calendar className="w-4 h-4" />
                                Meetings not appearing
                            </div>
                            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                                Open Integrations in Settings and use Sync Now under Calendly.
                            </p>
                        </article>
                    </div>
                </SectionShell>

                <section className="py-14 md:py-20 bg-white border-y border-gray-200">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-gray-900">
                            Connect your integrations now
                        </h2>
                        <p className="mt-4 text-sm sm:text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
                            Go to Settings and finish setup in a few minutes. Once connected,
                            your team can manage SMS, email, and meetings from one dashboard.
                        </p>
                        <div className="mt-8">
                            <Link
                                to="/settings?section=integrations"
                                className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg text-sm sm:text-base font-medium hover:bg-gray-800 transition-colors"
                            >
                                Open Settings - Integrations
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}

export default IntegrationsDocs;